import { useState, useEffect, useCallback } from 'react';
import { History, Search, RefreshCw, Store, Warehouse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { inventoryAPI } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

export const ADJUSTMENT_REASONS = [
  { value: 'physical_count', label: 'Physical count variance' },
  { value: 'damage', label: 'Damage' },
  { value: 'theft_loss', label: 'Theft / Loss' },
  { value: 'expiry', label: 'Expiry' },
  { value: 'data_correction', label: 'Data entry correction' },
  { value: 'other', label: 'Other' },
];

export const reasonLabel = (value) => ADJUSTMENT_REASONS.find(r => r.value === value)?.label || (value ? value : '-');

export const AdjustmentHistory = ({ lockedLocationType, lockedLocationId, stores = [], warehouses = [] }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [locationType, setLocationType] = useState(lockedLocationType || 'all');
  const [locationId, setLocationId] = useState(lockedLocationId || 'all');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (locationType !== 'all') params.location_type = locationType;
      if (locationId !== 'all') params.location_id = locationId;
      if (search.trim()) params.search = search.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await inventoryAPI.getStockAdjustments(params);
      setRows(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [locationType, locationId, search, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const locations = locationType === 'store' ? stores : locationType === 'warehouse' ? warehouses : [];

  return (
    <div className="space-y-4" data-testid="adjustment-history">
      <div className="flex flex-wrap items-end gap-3">
        {!lockedLocationType && (
          <div>
            <Label className="text-xs text-gray-500">Location type</Label>
            <Select value={locationType} onValueChange={(v) => { setLocationType(v); setLocationId('all'); }}>
              <SelectTrigger className="w-40 mt-1 bg-white" data-testid="history-location-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="warehouse">Warehouse</SelectItem>
                <SelectItem value="store">Store</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {!lockedLocationId && locations.length > 0 && (
          <div>
            <Label className="text-xs text-gray-500">{locationType === 'store' ? 'Store' : 'Warehouse'}</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger className="w-56 mt-1 bg-white" data-testid="history-location-id"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}{l.code ? ` (${l.code})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs text-gray-500">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1 bg-white w-40" data-testid="history-from-date" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1 bg-white w-40" data-testid="history-to-date" />
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Product, SKU or reason..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white" data-testid="history-search" />
        </div>
        <Button variant="outline" onClick={load} className="border-gray-300" data-testid="history-refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <History className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No adjustments found</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Date</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Before → After</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.id || i} data-testid={`history-row-${i}`}>
                  <TableCell className="text-sm text-gray-600 whitespace-nowrap">{formatDate(r.created_at)}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1.5">
                      {r.location_type === 'store' ? <Store className="w-3.5 h-3.5 text-emerald-500" /> : <Warehouse className="w-3.5 h-3.5 text-teal-500" />}
                      <span>{r.location_name || r.location_type}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-gray-800 text-sm">{r.product_name}</p>
                    <p className="text-xs text-teal-600">{r.product_sku}</p>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{r.batch_number || '-'}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={r.adjustment_type === 'add' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {r.adjustment_type === 'add' ? 'Added' : 'Removed'}
                    </Badge>
                  </TableCell>
                  <TableCell className={`text-right font-semibold ${r.adjustment_type === 'add' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {r.adjustment_type === 'add' ? '+' : '-'}{r.quantity}
                  </TableCell>
                  <TableCell className="text-right text-sm text-gray-600 whitespace-nowrap">
                    {r.previous_qty != null ? `${r.previous_qty} → ${r.new_qty}` : '-'}
                  </TableCell>
                  <TableCell className="text-sm max-w-xs">
                    {r.reason_category && <Badge variant="outline" className="mr-1 mb-1">{reasonLabel(r.reason_category)}</Badge>}
                    <span className="text-gray-600 break-words">{r.reason}</span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 whitespace-nowrap">{r.created_by_name || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {total > rows.length && (
        <p className="text-xs text-gray-500 text-right">Showing {rows.length} of {total} — narrow the filters to see more</p>
      )}
    </div>
  );
};
